<template>
  <div id="map-viewer-menu" :style="{ height: sheetHeight, left: `${insetLeft}px` }">
    <div id="menu-bar">
      <div id="sheet-controls">
        <button
          v-for="u in sizeOptions"
          :key="u.value"
          type="button"
          class="sheet-btn sheet-size"
          :class="{ 'sheet-size-aktif': level === u.value }"
          @click="$emit('set-sheet', u.value)">
          {{ u.label }}
        </button>
        <button type="button" class="sheet-btn" @click="$emit('set-sheet', 'closed')">Close</button>
      </div>

      <div v-if="menuActive === 0" id="datalog-header" class="d-flex align-items-center">
        <div class="ml-3">
          <select name="" class="form-control" v-model="unit.speed">
            <option value="knot">KNOT</option>
            <option value="km/h">KM/H</option>
          </select>
        </div>
        <div class="ml-3">
          <select name="" class="form-control" v-model="unit.distance">
            <option value="nm">NM</option>
            <option value="km">KM</option>
          </select>
        </div>
        <div class="ml-3">
          <select name="" class="form-control" v-model="dataSort">
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
        <div class="ml-3">
          <button class="btn btn-outline-primary" @click="showShowHideModal">
            Filter Column
          </button>
        </div>
        <div class="ml-3">
          <button class="btn btn-success" @click="onDownloadExcel">
            Download XLSX
          </button>
        </div>
      </div>

      <div v-if="menuActive === 1" id="chart-header">
        <div
          v-for="tab in chartTabs"
          :key="tab.index"
          class="chart-selector"
          :class="{ 'active-chart-selector': chartActive === tab.index }"
          @click="chartActive = tab.index">
          {{ tab.label }}
        </div>
      </div>
    </div>
    <div id="menu-content">
      <div id="content-selector">
        <div class="selector" :class="{ 'active-selector': menuActive === 0 }" @click="menuActive = 0">
          <img :src="menuActive === 0 ? icon.TableIconLight : icon.TableIcon" width="75%" />
        </div>
        <div class="selector" :class="{ 'active-selector': menuActive === 1 }" @click="menuActive = 1">
          <img :src="menuActive === 1 ? icon.ChartLineIconLight : icon.ChartLineIcon" width="75%" />
        </div>
      </div>

      <div v-if="menuActive === 0" id="datalog-content">
        <datalog-table
          ref="datalogTable"
          :asset="currAsset"
          :series="datalogSeries"
          interval="hour"
          :unit="unit"
          :export-file-name="exportFileName"
          @row-hover="onRowHover" />
      </div>

      <div v-if="menuActive === 1" id="chart-content">
        <RpmFuelSpeed v-if="chartActive === 0" :series="currAssetSummary.series" :deviceStatus="currCapability" />
        <FuelCharacteristics v-if="chartActive === 1" :series="currAssetSummary.series" type="PORT" :deviceStatus="currCapability" />
        <FuelCharacteristics v-if="chartActive === 2" :series="currAssetSummary.series" type="STARBOARD" :deviceStatus="currCapability" />
        <AuxiliaryEngineUsage v-if="chartActive === 3" :series="currAssetSummary.series" :deviceStatus="currCapability" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { formatDateTime } from '@/util/date';
import { buildFmsSeries } from '@/util/ship/fms-summary';
import { capabilityFromSummary } from '@/util/summary/seriesToSummaryData';
import { publish, HOVER } from '@/util/chart/chartSync';

import DatalogTable from '@/components/datalog/DatalogTable';
import AuxiliaryEngineUsage from '@/components/charts/AuxiliaryEngineUsage';
import RpmFuelSpeed from '@/components/charts/RpmFuelSpeed';
import FuelCharacteristics from '@/components/charts/FuelCharacteristics';

import CloseMenuIcon from '@/assets/icons/close-menu-icon.svg';
import ChartLineIcon from '@/assets/icons/chart-line-icon.svg';
import ChartLineIconLight from '@/assets/icons/chart-line-icon-light.svg';
import TableIcon from '@/assets/icons/table-icon.svg';
import TableIconLight from '@/assets/icons/table-icon-light.svg';

export default {
  name: 'MapViewerMenu',
  props: {
    level: {
      type: String,
      default: 'small',
    },
    // Sheet mulai setelah panel kiri biar kolom pertama tabel gak ketutup.
    insetLeft: {
      type: Number,
      default: 0,
    },
  },
  components: {
    DatalogTable,
    AuxiliaryEngineUsage,
    RpmFuelSpeed,
    FuelCharacteristics,
  },
  data() {
    return {
      icon: {
        CloseMenuIcon,
        ChartLineIcon,
        ChartLineIconLight,
        TableIcon,
        TableIconLight,
      },
      menuActive: 0,
      chartActive: 0,
      unit: {
        speed: 'knot',
        distance: 'nm',
      },
      sizeOptions: [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium' },
        { value: 'large', label: 'Large' },
      ],
      dataSort: 'oldest',
      screenWidth: window.innerWidth,
    };
  },
  mounted() {
    this._onResize = () => {
      this.screenWidth = window.innerWidth;
    };
    window.addEventListener('resize', this._onResize);
  },
  computed: {
    sheetHeight() {
      const isNarrow = this.screenWidth < 768;
      if (this.level === 'large') return isNarrow ? '85vh' : '70vh';
      if (this.level === 'medium') return isNarrow ? '65vh' : '55vh';
      return isNarrow ? '45vh' : '375px';
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    currCapability() {
      return capabilityFromSummary(this.currAsset?.deviceStatus, this.currAsset?.summary);
    },
    fmGroupNames() {
      return (this.currCapability.flowmeterGroups || []).map((g) => g.name);
    },
    chartTabs() {
      const tabs = [{ index: 0, label: 'RPM, Fuel Cons, Speed' }];
      if (this.fmGroupNames.includes('PORT')) tabs.push({ index: 1, label: 'Port Fuel Characteristic' });
      if (this.fmGroupNames.includes('STARBOARD')) tabs.push({ index: 2, label: 'STBD Fuel Characteristic' });
      if (this.currCapability.aeCount > 0) tabs.push({ index: 3, label: 'Auxiliary Engine Usage' });
      return tabs;
    },
    currAsset() {
      const currMassId = this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;

      return assets.find((asset) => asset.massId === currMassId);
    },
    currAssetSummary() {
      if (!this.currAsset?.summary) return null;

      return {
        series: buildFmsSeries({
          summary: this.currAsset.summary,
          summarySource: this.currAsset.summarySource,
          dateRange: this.dateRange,
          interval: 'hour',
          intervalSecondsOverride: 60 * 60,
          roundingStrategy: 'legacy-hour',
          normalizeHourlyTimestamps: true,
          dataSort: this.dataSort,
        }),
      };
    },
    datalogSeries() {
      return this.currAssetSummary?.series || [];
    },
    exportFileName() {
      const { start, end } = this.dateRange;
      const cap = (v) => formatDateTime(v, 'DD-MM-YYYY HH:mm', null, false);
      return `${this.currAsset?.massName || 'asset'} - ${cap(start)} - ${cap(end)}`;
    },
  },
  watch: {
    // Baris tabel dibuang v-if / di-rebuild pas sort ganti, dan Vue gak nembak
    // mouseleave buat DOM yang dihapus. Tanpa reset ini kursor peta nyangkut
    // di baris terakhir yang sempat di-hover.
    menuActive() {
      publish(HOVER, null);
    },
    dataSort() {
      publish(HOVER, null);
    },
    // Kapal ganti: tab yang sensornya ilang jangan ditinggalin aktif.
    chartTabs(next) {
      if (next.some((t) => t.index === this.chartActive)) return;
      this.chartActive = next.length ? next[0].index : 0;
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
    publish(HOVER, null);
  },
  methods: {
    onRowHover(ms) {
      publish(HOVER, ms);
    },
    showShowHideModal() {
      this.$refs.datalogTable.showColumnModal();
    },
    async onDownloadExcel() {
      await this.$refs.datalogTable.downloadExcel();
    },
  },
};
</script>

<style scoped>
#map-viewer-menu {
  position: absolute;
  right: 0;
  bottom: 0px;
  background-color: #FFFFFF;
  z-index: 1050;
}

#sheet-controls {
  display: flex;
  align-items: center;
  border-right: 2px solid #E8ECF3;
  padding: 4px 8px;
  white-space: nowrap;
}

.sheet-btn {
  border: 0;
  border-radius: 6px;
  background: #ff69b4;
  color: #ffffff;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 12px;
  margin-right: 6px;
}

/* Cuma yang kepilih yang berwarna, biar kebaca sebagai satu set pilihan. */
.sheet-size {
  background: #e5e7eb;
  color: #374151;
  padding: 4px 10px;
  margin-right: 4px;
}

.sheet-size-aktif {
  background: #ff69b4;
  color: #ffffff;
}

#menu-bar {
  height: 55px;
  background-color: #F2F4F4;
  display: flex;
}

#close-menu {
  cursor: pointer;
  color: #E5398C;
  border-right: 2px solid #E8ECF3;
  width: 55px;
  text-align: center;
  font-size: 23px;
  padding: 4px;
}

#menu-content {
  display: flex;
  flex-direction: row;
  height: calc(100% - 55px);
}

#content-selector {
  display: flex;
  flex-direction: column;
  min-width: 55px;
  max-width: 55px;
}

#chart-header {
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #E8ECF3;
  padding: 0;
}

.chart-selector {
  padding: 13.5px 15px;
  text-align: center;
  align-items: center;
  cursor: pointer;
  color: #2c3e50;
}

.active-chart-selector {
  background-color: #E5398C;
  color: white !important;
}

#chart-content {
  overflow-x: auto;
  width: 100%;
  height: 100%;
  position: relative;
}

.selector {
  cursor: pointer;
  border-bottom: 2px solid #E8ECF3;
  padding: 5px 10px;
  text-align: center;
}

.active-selector {
  background-color: #E5398C;
}

#datalog-content {
  overflow: auto;
  height: 100%;
  width: 100%;
  position: relative;
}
</style>
